import { Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";

import { AttachmentViewModel, IDocument } from "@tylertech/tyler-api-types";

import { OmniBarService } from "./omniBarService";
import { getLoggerFor } from "@tylertech/logging";

/** ! NOTE - the service has a dependency on a resource that is still dependent on AngularJs. It cannot be injected into any NgModule class constructors yet. */
@Injectable()
export class FileService
{
    private readonly $omniBarService = inject(OmniBarService);

    private readonly log = getLoggerFor("FileService");
    private readonly fileDownloadedSubject = new Subject<void>();

    readonly fileDownloaded: Observable<void>;

    constructor()
    {
        this.fileDownloaded = this.fileDownloadedSubject.asObservable();
    }

    private docToBlob(doc: IDocument): Blob
    {
        const data = doc.Content;
        const fileType = doc.MimeType;
        return doc["$Blob"]
            ? doc["$Blob"]
            : this.b64toBlob(data, fileType, true);
    }

    b64toBlob(b64Data: string, contentType: string, broadcast?: boolean): Blob
    {
        contentType = contentType || "";
        const sliceSize = 512;
        const byteCharacters = atob(b64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize)
        {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++)
            {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        if (broadcast)
        {
            this.fireFileDownloaded();
            this.$omniBarService.setCommandMenuBusy(false);
        }

        return blob;
    }

    convertToBase64String(file: File): Promise<string>
    {
        return new Promise((resolveFn, rejectFn) =>
        {
            const reader = new FileReader();
            reader.onload = (_event: Event) =>
            {
                const result = reader.result;
                this.log.debug(`result = ${JSON.stringify(result)}`);
                if (typeof result === "string")
                {
                    resolveFn(result);
                    return;
                }
                const uInt8Array = new Uint8Array(reader.result as ArrayBuffer);
                let charCode = "";
                for (let i = 0; i < uInt8Array.length; ++i)
                {
                    charCode += String.fromCharCode(uInt8Array[i]);
                }
                const data = btoa(charCode);
                this.log.information(`COMPLETED LOADING FILE: ${file.name} as ${typeof data}, size of payload: ${data.length}`);
                resolveFn(data);
            };
            reader.onloadstart = (_event: Event) =>
            {
                return this.log.information(`BEGIN READING FILE: ${file.name}`);
            };
            reader.onerror = reader.onabort = (_event: Event) =>
            {
                this.log.information("ERROR READING FILE: " + file.name);
                rejectFn("ERROR READING FILE: " + file.name);
            };
            reader.readAsDataURL(file);
        });
    }

    createFile(attachment: AttachmentViewModel): File
    {
        try
        {
            const parts: Array<BlobPart> = [
                attachment.Data,
            ];
            const prop: FilePropertyBag = {
                type: attachment.Type,
                lastModified: null,
            };
            return new File(parts, attachment.Filename, prop);
        }
        catch (error)
        {
            const blob = this.b64toBlob(attachment.Data, attachment.Type, false);
            const prop: FilePropertyBag = {
                type: attachment.Type,
                lastModified: Date.now(),
            };
            return new File([blob], attachment.Filename, prop);
        }
    }

    private fireFileDownloaded(): void
    {
        this.fileDownloadedSubject.next();
    }

    getDocumentLink(doc: IDocument): string
    {
        const blob = this.docToBlob(doc);

        // TODO - Why do we have to append a HTMLAnchorElement to the document to return a string?
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);

        document.body.appendChild(link);
        link.setAttribute("type", "hidden");
        link["download"] = doc.Filename;

        return link.href;
    }

    openDocument(doc: IDocument): void
    {
        const blob = this.docToBlob(doc);

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);

        document.body.appendChild(link);
        link.setAttribute("type", "hidden");
        link["download"] = doc.Filename;

        link.click();
        URL.revokeObjectURL(link.href);
    }

    openDocumentLink(data: string, fileType: string, fileName: string): void
    {
        const blob = this.b64toBlob(data, fileType, true);

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);

        document.body.appendChild(link);
        link.setAttribute("type", "hidden");
        link["download"] = fileName;

        link.click();
        URL.revokeObjectURL(link.href);
    }
}
